import { TrendingUpIcon, TrendingDownIcon, MinusIcon } from "lucide-react";
import type { SessionAttempt } from "@type/training";
import { cn } from "@util/tailwind";

interface AttemptHistoryProps {
    attempts: SessionAttempt[];
    currentToken?: string;
    onViewAttempt?: (token: string) => void;
    variant?: "default" | "sidebar";
    // When true (employer view), attempts that were only invited and never
    // started have nothing to show — opening one would bounce to the trainee
    // welcome screen — so they're rendered disabled / non-clickable.
    isEmployer?: boolean;
}

function gradeColor(score: number): { bg: string; text: string; ring: string } {
    if (score >= 84) return { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" };
    if (score >= 68) return { bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200" };
    if (score >= 52) return { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200" };
    return { bg: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200" };
}

function formatDateLong(dateStr: string | null): string {
    if (!dateStr) return "In progress";
    return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

function formatDateShort(dateStr: string | null): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

function statusLabel(status: string): { label: string; className: string } {
    switch (status) {
        case "completed":
            return { label: "Completed", className: "bg-emerald-100 text-emerald-700" };
        case "in_progress":
            return { label: "In Progress", className: "bg-blue-100 text-blue-700" };
        case "invited":
            return { label: "Invited", className: "bg-gray-100 text-gray-600" };
        case "expired":
            return { label: "Expired", className: "bg-amber-100 text-amber-700" };
        case "abandoned":
            return { label: "Abandoned", className: "bg-rose-100 text-rose-700" };
        default:
            return { label: status, className: "bg-gray-100 text-gray-600" };
    }
}

export function AttemptHistory({
    attempts,
    currentToken,
    onViewAttempt,
    variant = "default",
    isEmployer = false,
}: AttemptHistoryProps) {
    // An invited-but-never-started attempt has no results; for the employer it
    // must not be openable (it would redirect to the trainee welcome screen).
    const isDisabled = (status: string) => isEmployer && status === "invited";
    if (!attempts.length) {
        return <p className="text-sm text-gray-500">No attempts recorded yet.</p>;
    }

    const sorted = [...attempts].sort((a, b) => b.attempt_number - a.attempt_number);
    const byNumber = new Map(attempts.map((a) => [a.attempt_number, a]));

    if (variant === "sidebar") {
        return (
            <div className="flex flex-col">
                {sorted.map((attempt) => {
                    const isCurrent = attempt.token === currentToken;
                    const prevAttempt = byNumber.get(attempt.attempt_number - 1);
                    const delta =
                        attempt.total_score !== null &&
                        prevAttempt?.total_score !== null &&
                        prevAttempt?.total_score !== undefined
                            ? attempt.total_score - prevAttempt.total_score
                            : null;

                    const disabled = isDisabled(attempt.status);
                    return (
                        <div
                            key={attempt.token}
                            className={cn(
                                "px-4 py-3 transition-colors border-l-2",
                                disabled
                                    ? "cursor-not-allowed opacity-50 border-l-transparent"
                                    : isCurrent
                                    ? "cursor-pointer bg-blue-50 border-l-blue-600"
                                    : "cursor-pointer border-l-transparent hover:bg-gray-50",
                            )}
                            onClick={disabled ? undefined : () => onViewAttempt?.(attempt.token)}
                        >
                            <div className="text-sm font-medium text-gray-800">
                                Attempt {attempt.attempt_number}
                            </div>
                            <div className="text-[11px] text-gray-400 mt-0.5">
                                {attempt.completed_at
                                    ? `Completed on ${formatDateShort(attempt.completed_at)}`
                                    : attempt.status === "started" ||
                                      attempt.status === "in_progress" ||
                                      attempt.status === "scoring" ||
                                      attempt.status === "completed"
                                    ? "Scoring…"
                                    : statusLabel(attempt.status).label}
                            </div>
                            {attempt.total_score !== null && (
                                <div className="flex items-center gap-1.5 mt-1">
                                    <span className="text-xs font-semibold tabular-nums text-gray-600">
                                        {attempt.total_score}/100
                                    </span>
                                    {delta !== null && delta !== 0 && (
                                        <span
                                            className={cn(
                                                "text-xs font-medium tabular-nums",
                                                delta > 0
                                                    ? "text-emerald-600"
                                                    : "text-rose-600",
                                            )}
                                        >
                                            {delta > 0 ? `+${delta}` : `${delta}`}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    // default variant
    if (attempts.length === 1) {
        return <p className="text-sm text-gray-500">First attempt</p>;
    }

    return (
        <div className="flex flex-col gap-0">
            {sorted.map((attempt, idx) => {
                const isCurrent = attempt.token === currentToken;
                const prevAttempt = byNumber.get(attempt.attempt_number - 1);
                const delta =
                    attempt.total_score !== null &&
                    prevAttempt?.total_score !== null &&
                    prevAttempt?.total_score !== undefined
                        ? attempt.total_score - prevAttempt.total_score
                        : null;
                const grade =
                    attempt.total_score !== null ? gradeColor(attempt.total_score) : null;
                const status = statusLabel(attempt.status);
                const isLast = idx === sorted.length - 1;
                const disabled = isDisabled(attempt.status);

                return (
                    <div
                        key={attempt.token}
                        className={cn(
                            "relative flex items-center gap-4 rounded-lg px-3 py-3 transition-colors",
                            onViewAttempt && !disabled && "cursor-pointer hover:bg-gray-50",
                            disabled && "cursor-not-allowed opacity-50",
                            isCurrent && "ring-2 ring-blue-500",
                        )}
                        onClick={disabled ? undefined : () => onViewAttempt?.(attempt.token)}
                    >
                        <div className="relative flex flex-col items-center">
                            <div
                                className={cn(
                                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                                    isCurrent
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-100 text-gray-600",
                                )}
                            >
                                {attempt.attempt_number}
                            </div>
                            {!isLast && (
                                <div className="absolute top-8 w-px h-full bg-gray-200" />
                            )}
                        </div>

                        <div className="flex flex-1 flex-col gap-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-gray-700">
                                    {formatDateLong(attempt.completed_at)}
                                </span>
                                <span
                                    className={cn(
                                        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                                        status.className,
                                    )}
                                >
                                    {status.label}
                                </span>
                            </div>
                            {attempt.total_score !== null && grade && (
                                <div className="flex items-center gap-2">
                                    <span
                                        className={cn(
                                            "inline-flex rounded px-1.5 py-0.5 text-sm font-bold tabular-nums ring-1",
                                            grade.bg,
                                            grade.text,
                                            grade.ring,
                                        )}
                                    >
                                        {attempt.total_score}/100
                                    </span>
                                    {delta !== null && delta !== 0 && (
                                        <span
                                            className={cn(
                                                "text-sm font-medium tabular-nums",
                                                delta > 0
                                                    ? "text-emerald-600"
                                                    : "text-rose-600",
                                            )}
                                        >
                                            {delta > 0 ? `+${delta}` : delta}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {delta !== null && (
                            <div className="shrink-0">
                                {delta > 0 && (
                                    <TrendingUpIcon className="h-5 w-5 text-emerald-500" />
                                )}
                                {delta < 0 && (
                                    <TrendingDownIcon className="h-5 w-5 text-rose-500" />
                                )}
                                {delta === 0 && (
                                    <MinusIcon className="h-5 w-5 text-gray-400" />
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
